import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConfigService } from '@core/services/config.service';
import { LiteService } from '@core/services/lite.service';

@Component({
  selector: 'mfp-number-selector',
  templateUrl: './number-selector.component.html',
  styleUrls: ['./number-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberSelectorComponent),
      multi: true
    }
  ]
})
export class NumberSelectorComponent implements ControlValueAccessor {
  @Input() id = '';
  @Input() name = '';
  @Input() label = '';
  @Input() showAdvice = false;
  @Input() currency = '';
  @Input() amountIncrement = 1;
  /**
   * The value to use to increment if the 'amountIncrementExThreshold' is surpassed.
   */
  @Input() amountIncrementEx = 111;
  /**
   * The threshold of the times the longTouch directive is run before starting
   * to use the 'amountIncrementEx' value.
   */
  @Input() amountIncrementExThreshold = 40;
  @Input() max = 100;
  @Input() min = 0;
  @Input() loading = false;
  @Input() readonly = false;
  @Input() disabled = false;

  currencyPosition: 'left' | 'right' = 'right';

  innerValue = 0;

  constructor(private configService: ConfigService, private liteService: LiteService) {
    this.currencyPosition = this.configService.config?.currencyPosition;
  }

  get value(): number {
    return Math.trunc(this.innerValue);
  }

  set value(value: number) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(this.innerValue);
      this.onTouchCallback();
    }
  }

  get smoothMin(): number {
    return Math.floor(this.min);
  }

  get smoothMax(): number {
    return Math.ceil(this.max);
  }

  // ControlValueAccessor
  private onChangeCallback = (_: number) => {};
  private onTouchCallback = () => {};

  writeValue(value: number): void {
    this.value = value;
  }

  registerOnChange(fn: (_: number) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchCallback = fn;
  }

  add(times?: number) {
    if (this.innerValue !== this.max && !this.disabled && !this.readonly) {
      let amount = this.amountIncrement;
      if (this.innerValue + amount >= this.max) {
        this.value = this.max;
      } else {
        if (times && times > this.amountIncrementExThreshold) {
          amount = this.amountIncrementEx;
        }
        this.value = this.innerValue + amount;
      }
      this.liteService.coverageValueInsuredAmount.emit(true);
    }
  }

  subtract(times?: number) {
    if (this.innerValue !== this.min && !this.disabled && !this.readonly) {
      let amount = this.amountIncrement;
      if (this.innerValue + amount <= this.min) {
        this.value = this.min;
      } else {
        if (times && times > this.amountIncrementExThreshold) {
          amount = this.amountIncrementEx;
        }
        this.value = this.innerValue - amount;
      }
      this.liteService.coverageValueInsuredAmount.emit(true);
    }
  }

  changeCoverageInput(): void {
    this.liteService.coverageValueInsuredAmount.emit(true);
  }
}
